interface Asset {
  id: number;
  asset_id?: number | null;
  product_name: string;
  asset_category_id: number;
  category: {
    id: number;
    name: string;
    level: number;
    path: Array<{
      id: number;
      name: string;
      level: number;
      is_deleted: boolean;
    }>;
    breadcrumb: string;
  };
  overall_status: "assigned" | "in_stock" | "idle" | "lost";
  asset_exists: boolean;
  product_status: any;
  assigned_to_type: any;
}

interface CategoryNode {
  id: number;
  name: string;
  status: string;
  category_level: number;
  parentId?: number | null;
  subcategories?: CategoryNode[];
}

interface ChartDataItem {
  name: string;
  assigned: number;
  inStock: number;
  inActive: number;
  total: number;
  categoryId: number;
}

// Helper function to flatten categories and get specific level
const getCategoriesAtLevel = (
  categories: CategoryNode[],
  targetLevel: number
): CategoryNode[] => {
  const result: CategoryNode[] = [];

  const traverse = (cats: CategoryNode[]) => {
    cats.forEach((cat) => {
      if (cat.status === "Active" && cat.category_level === targetLevel) {
        result.push(cat);
      }
      if (cat.subcategories && cat.subcategories.length > 0) {
        traverse(cat.subcategories);
      }
    });
  };

  traverse(categories);
  return result;
};

// Helper function to get direct subcategories of a specific category
const getDirectSubcategories = (
  categories: CategoryNode[],
  parentId: number
): CategoryNode[] => {
  const result: CategoryNode[] = [];

  const findAndGetChildren = (cats: CategoryNode[]): boolean => {
    for (const cat of cats) {
      if (cat.id === parentId) {
        if (cat.subcategories) {
          result.push(
            ...cat.subcategories.filter((sub) => sub.status === "Active")
          );
        }
        return true;
      }
      if (cat.subcategories && cat.subcategories.length > 0) {
        if (findAndGetChildren(cat.subcategories)) {
          return true;
        }
      }
    }
    return false;
  };

  findAndGetChildren(categories);
  return result;
};

// Helper function to check if an asset belongs to a category (including subcategories)
const assetBelongsToCategory = (asset: Asset, categoryId: number): boolean => {
  if (asset.category?.path) {
    return asset.category.path.some((pathCat) => pathCat.id === categoryId);
  }
  return asset.asset_category_id === categoryId;
};

// Helper function to count stats for a category
const countStatsForCategory = (
  assets: Asset[],
  categoryId: number
): ChartDataItem => {
  let assigned = 0;
  let inStock = 0;
  let inActive = 0;
  let total = 0;

  assets.forEach((asset) => {
    if (assetBelongsToCategory(asset, categoryId)) {
      total++;
      if (asset.overall_status === "lost") {
        inActive++;
      } else if (
        asset.assigned_to_type !== "not_assigned" &&
        asset.assigned_to_type !== "not_created"
      ) {
        assigned++;
      } else if (asset.product_status == "Active") {
        inStock++;
      } else {
        inActive++;
      }
    }
  });

  return { assigned, inStock, total, inActive } as ChartDataItem;
};

export const calculateChartData = (
  assets: Asset[],
  assetCategories: CategoryNode[], // Complete category structure
  selectedCategoryId?: string | null
): ChartDataItem[] => {
  if (!selectedCategoryId) {
    const rootCategories = getCategoriesAtLevel(assetCategories, 0);
    const chartData: ChartDataItem[] = rootCategories.map((category) => {
      const stats = countStatsForCategory(assets, category.id);

      return {
        name: category.name,
        assigned: stats.assigned,
        inStock: stats.inStock,
        inActive: stats.inActive,
        total: stats.total,
        categoryId: category.id,
      };
    });
    return chartData.sort(
      (a, b) => b.total - a.total || a.name.localeCompare(b.name)
    );
  } else {
    const categoryId = parseInt(selectedCategoryId);
    const subcategories = getDirectSubcategories(assetCategories, categoryId);
    if (subcategories.length === 0) {
      const leafCategory = findCategoryById(assetCategories, categoryId);
      if (!leafCategory) return [];

      const stats = countStatsForCategory(assets, categoryId);

      return [
        {
          name: leafCategory.name,
          assigned: stats.assigned,
          inStock: stats.inStock,
          total: stats.total,
          categoryId: leafCategory.id,
          inActive: stats.inActive,
        },
      ];
    }

    const chartData: ChartDataItem[] = subcategories.map((subcategory) => {
      const stats = countStatsForCategory(assets, subcategory.id);

      return {
        name: subcategory.name,
        assigned: stats.assigned,
        inStock: stats.inStock,
        total: stats.total,
        categoryId: subcategory.id,
        inActive: stats.inActive,
      };
    });

    return chartData.sort(
      (a, b) => b.total - a.total || a.name.localeCompare(b.name)
    );
  }
};

// Helper function to find a category by ID
const findCategoryById = (
  categories: CategoryNode[],
  categoryId: number
): CategoryNode | null => {
  for (const category of categories) {
    if (category.id === categoryId) {
      return category;
    }
    if (category.subcategories && category.subcategories.length > 0) {
      const found = findCategoryById(category.subcategories, categoryId);
      if (found) return found;
    }
  }
  return null;
};

// Helper to get the selected category name for chart title
export const getSelectedCategoryName = (
  assetCategories: CategoryNode[],
  selectedCategoryId?: string | null
): string => {
  if (!selectedCategoryId) {
    return "All Categories";
  }

  const categoryId = parseInt(selectedCategoryId);
  const category = findCategoryById(assetCategories, categoryId);

  return category ? category.name : "Not Found";
};

export const getMainStats = (assetsData: any[]) => {
  if (!assetsData || !Array.isArray(assetsData) || assetsData.length === 0) {
    return {
      total: 0,
      assigned: 0,
      instock: 0,
      inactive: 0,
      lost: 0,
      assignedPercentage: 0,
      instockPercentage: 0,
      inactivePercentage: 0,
      lostPercentage: 0,
      assignmentBreakdown: {
        users: 0,
        locations: 0,
        departments: 0,
        total: 0,
        userPercentage: 0,
        locationPercentage: 0,
        departmentPercentage: 0,
      },
    };
  }

  const stats = {
    total: assetsData.length,
    assigned: 0,
    instock: 0,
    inactive: 0,
    lost: 0,
  };

  const assignmentBreakdown = {
    users: 0,
    locations: 0,
    departments: 0,
    total: 0,
  };

  assetsData.forEach((asset) => {
    if (asset.overall_status === "lost") {
      stats.lost++;
    } else if (
      asset.assigned_to_type !== "not_assigned" &&
      asset.assigned_to_type !== "not_created"
    ) {
      stats.assigned++;
      switch (asset.assigned_to_type) {
        case "user":
          assignmentBreakdown.users++;
          break;
        case "location":
          assignmentBreakdown.locations++;
          break;
        case "department":
          assignmentBreakdown.departments++;
          break;
      }
    } else if (asset.product_status == "Active") {
      stats.instock++;
    } else {
      stats.inactive++;
    }
  });

  // Calculate total assigned assets
  assignmentBreakdown.total =
    assignmentBreakdown.users +
    assignmentBreakdown.locations +
    assignmentBreakdown.departments;

  // Calculate main percentages
  const assignedPercentage =
    stats.total > 0
      ? Number(((stats.assigned / stats.total) * 100).toFixed(2))
      : 0;

  const instockPercentage =
    stats.total > 0
      ? Number(((stats.instock / stats.total) * 100).toFixed(2))
      : 0;

  const inactivePercentage =
    stats.total > 0
      ? Number(((stats.inactive / stats.total) * 100).toFixed(2))
      : 0;

  const lostPercentage =
    stats.total > 0
      ? Number(((stats.lost / stats.total) * 100).toFixed(2))
      : 0;

  // Calculate assignment breakdown percentages (out of total assigned assets)
  const userPercentage =
    assignmentBreakdown.total > 0
      ? Number(
          (
            (assignmentBreakdown.users / assignmentBreakdown.total) *
            100
          ).toFixed(2)
        )
      : 0;

  const locationPercentage =
    assignmentBreakdown.total > 0
      ? Number(
          (
            (assignmentBreakdown.locations / assignmentBreakdown.total) *
            100
          ).toFixed(2)
        )
      : 0;

  const departmentPercentage =
    assignmentBreakdown.total > 0
      ? Number(
          (
            (assignmentBreakdown.departments / assignmentBreakdown.total) *
            100
          ).toFixed(2)
        )
      : 0;

  return {
    total: stats.total,
    assigned: stats.assigned,
    instock: stats.instock,
    inactive: stats.inactive,
    lost: stats.lost,
    assignedPercentage,
    instockPercentage,
    inactivePercentage,
    lostPercentage,
    assignmentBreakdown: {
      users: assignmentBreakdown.users,
      locations: assignmentBreakdown.locations,
      departments: assignmentBreakdown.departments,
      total: assignmentBreakdown.total,
      userPercentage,
      locationPercentage,
      departmentPercentage,
    },
  };
};
